<template>
  <component-wrapper v-if="!isAppView">
    <div
      v-if="!isEditMode"
      class="page-header-fi"
    >
      <header-fi
        :fields="fields"
        :logo-header="logoHeader"
        :logo-brand-area="logoBrandArea"
        :home-data="homeData"
        :navigation-data="navigationData"
        :service-navigation="serviceNavigation"
        :breadcrumb-data="breadcrumbData"
      />

      <chat-bot-bubble-fi
        v-if="chatBotEnabled"
        use-web-socket
      />
      <support-box-fi v-if="supportBoxEnabled" />
    </div>
    <div
      v-else
      class="page-header-fi--xpe"
    >
      <header-fi-xpe
        :fields="fields"
        :logo-header="logoHeader"
        :logo-brand-area="logoBrandArea"
        :breadcrumb-data="breadcrumbData"
      />
    </div>
    <process-add-to-cart-helper v-if="addToCartItems" />
  </component-wrapper>
  <template v-else>
    <snackbar-fi v-if="showAppViewSnackbar" />
    <toast-notification-fi />
  </template>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import HeaderFi from '@/components/header-fi/HeaderFi.vue';
import { useHeaderPage } from '@/components/header-fi/headerPage';
import ToastNotificationFi from '@/components/notifications-fi/toast-notification-fi/ToastNotificationFi.vue';
import SupportBoxFi from '@/components/Support-box-fi/SupportBoxFi.vue';
import { getItemsFromSession } from '@/composables/cartSession';
import { useDataLayer } from '@/composables/dataLayer';
import { useFischer } from '@/composables/fischerPlugin';
import type { PageHeaderFiFields } from '@/generated/fields';
import { useNavigationStore } from '@/store/pinia/navigation';

const ChatBotBubbleFi = defineAsyncComponent(
  () => import('@/components/chat-box-fi/chat-bot-bubble-fi/ChatBotBubbleFi.vue'),
);

const ProcessAddToCartHelper = defineAsyncComponent(
  () => import('@/components/header-fi/ProcessAddToCartHelper.vue'),
);

const HeaderFiXpe = defineAsyncComponent(
  () => import('@/components/header-fi/header-fi-xpe/HeaderFiXpe.vue'),
);

const SnackbarFi = defineAsyncComponent(
  () => import('@/components/shop-modules/snackbar-fi/SnackbarFi.vue'),
);

defineOptions({
  name: 'PageHeaderFi',
});

const props = withDefaults(
  defineProps<{
    fields?: PageHeaderFiFields | undefined;
    customData?: any;
  }>(),
  {
    fields: undefined,
    customData: () => null,
  },
);

useDataLayer();
const fischer = useFischer();
const navigationStore = useNavigationStore();
const { breadcrumbData, logoHeader, logoBrandArea, cData, homeData } = useHeaderPage(props);

const addToCartItems = ref(false);
onMounted(() => {
  if (fischer.shop.supportsCart() && getItemsFromSession()) {
    addToCartItems.value = true;
  }
});

const navigationData = computed(() => {
  return cData.value?.navigation?.home?.subElements || [];
});

const serviceNavigation = computed(() => {
  return cData.value?.serviceNavigation || [];
});

const chatBotEnabled = computed<boolean>(() => {
  return fischer.shop.isClosedShop() && fischer.isChatbotEnabled();
});

const supportBoxEnabled = computed<boolean>(() => {
  return fischer.isCatalogPage() && fischer.isSupportBoxEnabled();
});

const showAppViewSnackbar = computed<boolean>(
  () =>
    fischer.app.isAppView() &&
    (fischer.shop.isClosedShop() ||
      fischer.shop.isOnlineShop() ||
      fischer.shop.isRetailConnectCart()),
);

const setServiceNavigationMarker = () => {
  serviceNavigation.value.forEach((serviceItem: any, navItemIdx: number) => {
    if (!serviceItem?.elements) {
      return;
    }
    serviceItem.elements.forEach((element: any, elementIdx: number) => {
      if (!element.link) {
        return;
      }
      if (element.link.url === fischer.uri()) {
        const navIndex = navigationData.value.length + 10 * navItemIdx + elementIdx;
        navigationStore.setNavIndex(navIndex);
      }
    });
  });
};
setServiceNavigationMarker();
</script>

<style scoped src="./page-header-fi" />
